import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import TextDensityCompressIcon from '@atlaskit/icon-lab/core/text-density-compress';
import TextDensityExpandIcon from '@atlaskit/icon-lab/core/text-density-expand';

import type { UiContentMode } from '../utils/contentMode.ts';
import { useEditorContentMode } from '../hooks/useEditorContentMode.ts';

type Props = {
	contentId: string;
	disabled?: boolean;
	isStatusDisconnected?: boolean;
};

const messages = defineMessages({
	makeCompact: {
		id: 'editor-content-mode-button.make.compact',
		defaultMessage: 'Use compact density',
		description: 'Switch to compact content mode',
	},
	makeStandard: {
		id: 'editor-content-mode-button.make.standard',
		defaultMessage: 'Use standard density',
		description: 'Switch to standard content mode',
	},
});

export const EditorContentModeButton: React.FC<Props> = ({
	contentId,
	disabled = false,
	isStatusDisconnected = false,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { mode, isSaving, toggle } = useEditorContentMode({ contentId });
	const isCompact = mode === 'compact';

	const label = intl.formatMessage(isCompact ? messages.makeStandard : messages.makeCompact);
	const Icon = useMemo(
		() => (isCompact ? TextDensityExpandIcon : TextDensityCompressIcon),
		[isCompact],
	);

	const isBlocked = disabled || isStatusDisconnected || isSaving;

	const fireAnalytics = useCallback(
		(from: UiContentMode, to: UiContentMode) => {
			createAnalyticsEvent?.({
				type: 'sendTrackEvent',
				data: {
					action: 'updateContentMode',
					actionSubject: 'updateContentMode',
					source: 'EditorContentModeButton',
					attributes: { contentId, previousMode: from, mode: to },
				},
			}).fire();
		},
		[contentId, createAnalyticsEvent],
	);

	const handleClick = useCallback(async () => {
		if (!contentId || isStatusDisconnected || isSaving) return;
		const { ok, from, to } = await toggle();
		if (ok) fireAnalytics(from, to);
	}, [contentId, isStatusDisconnected, isSaving, toggle, fireAnalytics]);

	return (
		<IconButton
			appearance="subtle"
			spacing="default"
			isDisabled={isBlocked || isSaving}
			onClick={handleClick}
			isTooltipDisabled={false}
			label={label}
			icon={(props) => <Icon {...props} label={label} />}
			testId="editor-content-mode-button"
		/>
	);
};
